<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationCard from 'dashboard/components-next/Conversation/ConversationCard/ConversationCard.vue';

/**
 * @typedef {Object} ContactHistoryEvent
 * @property {string} id Stable identifier for the mocked event.
 * @property {string} icon Icon class shown in the activity timeline.
 * @property {string} accentClass Tailwind utility classes used for the icon chip.
 * @property {string} title Event title.
 * @property {string} description Event detail.
 * @property {string} occurredAt Mocked timestamp.
 * @property {string} meta Source, channel, or context.
 */

const props = defineProps({
  historyType: {
    type: String,
    default: 'conversations',
  },
});

const { t } = useI18n();
const route = useRoute();

/**
 * Builds mocked in-store visit events until visit tracking is persisted.
 *
 * @returns {ContactHistoryEvent[]}
 */
const storeVisitEvents = computed(() => [
  {
    id: 'store-visit-1',
    icon: 'i-lucide-store',
    accentClass: 'bg-n-ruby-3 text-n-ruby-11',
    title: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_ONE.TITLE'),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_ONE.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_ONE.OCCURRED_AT'
    ),
    meta: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_ONE.META'),
  },
  {
    id: 'store-visit-2',
    icon: 'i-lucide-sparkles',
    accentClass: 'bg-n-violet-3 text-n-violet-11',
    title: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_TWO.TITLE'),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_TWO.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_TWO.OCCURRED_AT'
    ),
    meta: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_TWO.META'),
  },
  {
    id: 'store-visit-3',
    icon: 'i-lucide-package-check',
    accentClass: 'bg-n-grass-3 text-n-grass-11',
    title: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_THREE.TITLE'),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_THREE.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_THREE.OCCURRED_AT'
    ),
    meta: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_THREE.META'),
  },
  {
    id: 'store-visit-4',
    icon: 'i-lucide-calendar-check',
    accentClass: 'bg-n-blue-3 text-n-blue-11',
    title: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_FOUR.TITLE'),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_FOUR.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_FOUR.OCCURRED_AT'
    ),
    meta: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_FOUR.META'),
  },
  {
    id: 'store-visit-5',
    icon: 'i-lucide-message-square',
    accentClass: 'bg-n-amber-3 text-n-amber-11',
    title: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_FIVE.TITLE'),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_FIVE.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_FIVE.OCCURRED_AT'
    ),
    meta: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.STORE_VISITS.VISIT_FIVE.META'),
  },
]);

/**
 * Builds mocked Shopify-style online events.
 *
 * @returns {ContactHistoryEvent[]}
 */
const onlineHistoryEvents = computed(() => [
  {
    id: 'online-product-opened',
    icon: 'i-lucide-eye',
    accentClass: 'bg-n-blue-3 text-n-blue-11',
    title: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.PRODUCT_OPENED.TITLE'
    ),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.PRODUCT_OPENED.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.PRODUCT_OPENED.OCCURRED_AT'
    ),
    meta: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.PRODUCT_OPENED.META'
    ),
  },
  {
    id: 'online-order',
    icon: 'i-lucide-receipt-text',
    accentClass: 'bg-n-grass-3 text-n-grass-11',
    title: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.ORDER_CREATED.TITLE'
    ),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.ORDER_CREATED.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.ORDER_CREATED.OCCURRED_AT'
    ),
    meta: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.ORDER_CREATED.META'),
  },
  {
    id: 'online-purchase',
    icon: 'i-lucide-credit-card',
    accentClass: 'bg-n-ruby-3 text-n-ruby-11',
    title: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.PURCHASE.TITLE'),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.PURCHASE.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.PURCHASE.OCCURRED_AT'
    ),
    meta: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.ONLINE_EVENTS.PURCHASE.META'),
  },
]);

/**
 * Builds mocked marketing events until Klaviyo events are normalized here.
 *
 * @returns {ContactHistoryEvent[]}
 */
const marketingHistoryEvents = computed(() => [
  {
    id: 'marketing-klaviyo-profile',
    icon: 'i-lucide-radio',
    accentClass: 'bg-n-violet-3 text-n-violet-11',
    title: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.KLAVIYO_PROFILE.TITLE'
    ),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.KLAVIYO_PROFILE.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.KLAVIYO_PROFILE.OCCURRED_AT'
    ),
    meta: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.KLAVIYO_PROFILE.META'
    ),
  },
  {
    id: 'marketing-email-opened',
    icon: 'i-lucide-mail-open',
    accentClass: 'bg-n-teal-3 text-n-teal-11',
    title: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.EMAIL_OPENED.TITLE'
    ),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.EMAIL_OPENED.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.EMAIL_OPENED.OCCURRED_AT'
    ),
    meta: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.EMAIL_OPENED.META'
    ),
  },
  {
    id: 'marketing-campaign-click',
    icon: 'i-lucide-mouse-pointer-click',
    accentClass: 'bg-n-amber-3 text-n-amber-11',
    title: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.CAMPAIGN_CLICK.TITLE'
    ),
    description: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.CAMPAIGN_CLICK.DESCRIPTION'
    ),
    occurredAt: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.CAMPAIGN_CLICK.OCCURRED_AT'
    ),
    meta: t(
      'CONTACTS_LAYOUT.SIDEBAR.HISTORY.MARKETING_EVENTS.CAMPAIGN_CLICK.META'
    ),
  },
]);

const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const contactsById = useMapGetter('contacts/getContactById');
const stateInbox = useMapGetter('inboxes/getInboxById');
const accountLabels = useMapGetter('labels/getLabels');

const accountLabelsValue = computed(() => accountLabels.value);

const uiFlags = useMapGetter('contactConversations/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);

const contactConversations = computed(() =>
  conversations.value(route.params.contactId)
);

const activeMockedEvents = computed(() => {
  if (props.historyType === 'storeVisits') return storeVisitEvents.value;
  if (props.historyType === 'onlineHistory') return onlineHistoryEvents.value;
  if (props.historyType === 'marketingHistory')
    return marketingHistoryEvents.value;

  return [];
});
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else class="flex flex-col gap-4 px-6 py-4">
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.SECTION_TITLE') }}
      </h3>
      <span
        v-if="historyType !== 'conversations'"
        class="px-2 py-1 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
      >
        {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.MOCK_BADGE') }}
      </span>
    </div>

    <section v-if="historyType === 'conversations'" class="flex flex-col">
      <div
        v-if="contactConversations.length > 0"
        class="-mx-3 divide-y divide-n-strong [&>*:hover]:!border-y-transparent [&>*:hover+*]:!border-t-transparent"
      >
        <ConversationCard
          v-for="conversation in contactConversations"
          :key="conversation.id"
          :conversation="conversation"
          :contact="contactsById(conversation.meta.sender.id)"
          :state-inbox="stateInbox(conversation.inboxId)"
          :account-labels="accountLabelsValue"
          class="rounded-none hover:rounded-xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
        />
      </div>
      <p v-else class="py-4 text-sm leading-6 text-center text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.EMPTY_STATE') }}
      </p>
    </section>

    <section v-else class="flex flex-col">
      <ol class="relative flex flex-col gap-1">
        <li
          v-for="event in activeMockedEvents"
          :key="event.id"
          class="relative flex gap-3 py-3 before:absolute before:bottom-0 before:top-10 before:w-px before:bg-n-weak ltr:before:left-4 rtl:before:right-4 last:before:hidden"
        >
          <span
            class="relative z-10 inline-flex items-center justify-center rounded-full size-8 shrink-0"
            :class="event.accentClass"
          >
            <span class="size-4" :class="event.icon" />
          </span>
          <div class="flex flex-col min-w-0 gap-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-xs text-n-slate-10">
                {{ event.occurredAt }}
              </span>
            </div>
            <p class="text-sm text-n-slate-12">
              {{ event.title }}
            </p>
            <p class="text-xs leading-5 text-n-slate-11">
              {{ event.description }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ event.meta }}
            </p>
          </div>
        </li>
      </ol>
    </section>
  </div>
</template>
